import React, { createContext, useContext, ReactNode } from "react";
import Dialog from "src/components/common/Dialog";
import ViewSentCardsDialog from "./ViewSentCardsDialog";
import { useSendCards as useSendCardsHook, MAX_SEND_CARDS_LIMIT } from "./useSendCards";
import { useViewSentCards as useViewSentCardsHook } from "./useViewSentCards";
import Button from "src/components/core/Button";


interface CardSendingContextType {
  sendCards: ReturnType<typeof useSendCardsHook>["sendCards"];
  closeCardDialog: ReturnType<typeof useSendCardsHook>["closeCardDialog"];
  isOpen: ReturnType<typeof useSendCardsHook>["isOpen"];
  viewSentCards: ReturnType<typeof useViewSentCardsHook>["viewSentCards"];
}

const CardSendingContext = createContext<CardSendingContextType | undefined>(
  undefined
);

export const useSendCards = () => {
  const context = useContext(CardSendingContext);
  if (!context) {
    throw new Error("useSendCards must be used within a SendCardsProvider");
  }
  return context;
};

interface SendCardsProviderProps {
  children: ReactNode;
}

const SendCardsProvider: React.FC<SendCardsProviderProps> = ({ children }) => {
  const {
    sendCards,
    confirmSendCards,
    closeCardDialog,
    isOpen,
    embedUrl,
    iframeRef,
    limitExceededInfo,
    validationInfo,
    isLoadingBalance,
    isFetchingBalance,
  } = useSendCardsHook();

  const {
    viewSentCards,
    closeDialog: closeViewDialog,
    isOpen: isViewOpen,
    previewUrl,
    isLoading: isViewLoading,
  } = useViewSentCardsHook();

  return (
    <CardSendingContext.Provider
      value={{ sendCards, closeCardDialog, isOpen, viewSentCards }}
    >
      {children}
      {limitExceededInfo ? (
        <Dialog
          isOpen={isOpen}
          onOpenChange={(open) => !open && closeCardDialog()}
          title="Card Limit Exceeded"
          className="!max-w-md"
        >
          <div className="p-4">
            <p className="text-gray-primary mb-4">
              You've selected <strong>{limitExceededInfo.cardCount.toLocaleString()}</strong> contacts,
              which exceeds the maximum of <strong>{MAX_SEND_CARDS_LIMIT.toLocaleString()}</strong> cards
              that can be sent at once.
            </p>
            <p className="text-gray-secondary mb-6">
              Please select fewer contacts or use filters to narrow down your selection.
            </p>
            <div className="flex justify-center">
              <Button onClick={closeCardDialog}>
                Close
              </Button>
            </div>
          </div>
        </Dialog>
      ) : validationInfo ? (
        <Dialog
          isOpen={isOpen}
          onOpenChange={(open) => !open && closeCardDialog()}
          title="Send Cards"
          className="!max-w-md"
        >
          <div className="p-4 text-center">
            {validationInfo.validCount === 0 ? (
              <>
                <p className="text-gray-primary mb-4">
                  {validationInfo.totalCount === 1
                    ? "The selected contact is missing address information and cannot receive a card."
                    : `All ${validationInfo.totalCount.toLocaleString()} selected contacts are missing address information and cannot receive cards.`}
                </p>
                <div className="flex justify-center">
                  <Button onClick={closeCardDialog}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-primary mb-1">
                  <strong>{validationInfo.validCount.toLocaleString()}</strong> of <strong>{validationInfo.totalCount.toLocaleString()}</strong> {validationInfo.totalCount === 1 ? "card" : "cards"} will be sent.
                </p>
                <p className="text-gray-secondary mb-4">
                  {validationInfo.skippedCount > 0 && validationInfo.duplicateCount > 0 ? (
                    <>{validationInfo.skippedCount.toLocaleString()} missing address information. {validationInfo.duplicateCount.toLocaleString()} {validationInfo.duplicateCount === 1 ? "duplicate" : "duplicates"} removed.</>
                  ) : validationInfo.skippedCount > 0 ? (
                    <>{validationInfo.skippedCount.toLocaleString()} {validationInfo.skippedCount === 1 ? "is" : "are"} missing address information.</>
                  ) : (
                    <>{validationInfo.duplicateCount.toLocaleString()} duplicate {validationInfo.duplicateCount === 1 ? "recipient" : "recipients"} removed.</>
                  )}
                </p>
                <div className="flex justify-center gap-3 mt-6">
                  <Button variant="outline" onClick={closeCardDialog} disabled={isFetchingBalance}>
                    Cancel
                  </Button>
                  <Button onClick={confirmSendCards} isLoading={isFetchingBalance}>
                    {`Continue with ${validationInfo.validCount.toLocaleString()} ${validationInfo.validCount === 1 ? "Card" : "Cards"}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Dialog>
      ) : (
        <Dialog
          isOpen={isOpen}
          onOpenChange={(open) => !open && closeCardDialog()}
          title=""
          className="card-sending-dialog !max-w-[1100px] !w-[95vw] md:!w-[1100px]"
        >
          <div className="w-full" style={{ height: "85vh" }}>
            {isLoadingBalance ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                  <p>Checking balance...</p>
                </div>
              </div>
            ) : embedUrl ? (
              <iframe
                ref={iframeRef}
                src={embedUrl}
                className="send-cards-iframe w-full h-full border-0 rounded-lg"
                title="Card Sending Application"
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            )}
          </div>
        </Dialog>
      )}
      <ViewSentCardsDialog
        isOpen={isViewOpen}
        onClose={closeViewDialog}
        previewUrl={previewUrl}
        isLoading={isViewLoading}
      />
    </CardSendingContext.Provider>
  );
};

export default SendCardsProvider;
